import {
  MenuSchema,
  ORDERS_PAUSED_CODE,
  OrderSchema,
  StoreCurrentResponseSchema,
} from "@toema/shared-types";
import type {
  CreateOrderRequest,
  Menu,
  Order,
  StoreCurrentResponse,
} from "@toema/shared-types";

/**
 * Every path here is RELATIVE, and that is load-bearing rather than tidy.
 *
 * The API resolves which store a request belongs to from the request's `Host`
 * header. A relative path is resolved by the browser against the page's own
 * origin, so loading the site as `http://bestkebab.localhost:5192` sends
 * `Host: bestkebab.localhost:5192`, and the dev proxy in `vite.config.ts`
 * forwards it to the API *without rewriting `Host`*. The backend strips the
 * port and finds the Best Kebab organization.
 *
 * Hardcoding `http://localhost:3002` here — the way Piconi does — would send
 * `Host: localhost:3002`, which the backend normalizes to `localhost`, which
 * is Piconi. The store would silently serve pizza. There is deliberately no
 * base-URL constant in this file to make that mistake with.
 */
const STORE_PATH = "/stores/current";
const MENU_PATH = "/menu";
const ORDERS_PATH = "/orders";

/** Identifies the caller to the API, as every other Toema frontend does. */
const FRONTEND_ID = "landing";

/** An API call that came back, but not with a 2xx. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function errorCodeIn(detail: string): string | null {
  try {
    const body: unknown = JSON.parse(detail);
    if (typeof body !== "object" || body === null || !("code" in body)) return null;
    return typeof body.code === "string" ? body.code : null;
  } catch {
    return null;
  }
}

export function isOrdersPaused(error: unknown): boolean {
  return error instanceof ApiError && error.code === ORDERS_PAUSED_CODE;
}

/**
 * Performs the request and hands back the decoded JSON as `unknown`.
 *
 * `unknown` rather than a generic is the point: the only way out of this
 * function is through one of the zod schemas below, so no response can reach a
 * component wearing a type nobody checked.
 */
async function requestJson(path: string, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  headers.set("x-frontend-id", FRONTEND_ID);
  if (init?.body !== undefined) headers.set("content-type", "application/json");

  const res = await fetch(path, { ...init, headers });

  if (!res.ok) {
    // The API's error shape is `{ code, message }`; fall back to the status
    // line when a proxy or a crash returns something else entirely.
    const detail = await res.text().catch(() => "");
    throw new ApiError(res.status, detail || res.statusText, errorCodeIn(detail));
  }

  return res.json();
}

/** The store itself — name, profile and theme — as resolved from this page's own hostname. */
export async function fetchStore(signal?: AbortSignal): Promise<StoreCurrentResponse> {
  const json = await requestJson(STORE_PATH, { signal });
  const parsed = StoreCurrentResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Store response did not match the expected shape: ${parsed.error.message}`);
  }
  return parsed.data;
}

/** The store's menu, as resolved from this page's own hostname. */
export async function fetchMenu(signal?: AbortSignal): Promise<Menu> {
  const json = await requestJson(MENU_PATH, { signal });
  const parsed = MenuSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Menu response did not match the expected shape: ${parsed.error.message}`);
  }
  return parsed.data;
}

/**
 * Writes a real order. This is the same endpoint the POS and the kitchen
 * display read from, so an order placed here shows up in both.
 */
export async function createOrder(body: CreateOrderRequest): Promise<Order> {
  const json = await requestJson(ORDERS_PATH, {
    method: "POST",
    body: JSON.stringify(body),
  });
  const parsed = OrderSchema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Order response did not match the expected shape: ${parsed.error.message}`);
  }
  return parsed.data;
}
